import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Copy,
  Save,
  Target,
  Wallet,
  AlertTriangle,
  TrendingUp,
  Calendar,
} from "lucide-react";

import { PageHeader } from "@/components/PageHeader";
import { useAuth } from "@/contexts/AuthContext";
import { useTenant } from "@/contexts/TenantContext";
import { api, formatApiError } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";

import { BranchFilter } from "@/components/reports/BranchFilter";
import { KpiCard, formatMxn, formatInt } from "@/components/reports/KpiCard";
import { ExportMenu } from "@/components/reports/ExportMenu";

const STATUS_META = {
  ok: { label: "OK", tone: "bg-emerald-100 text-emerald-800 border-emerald-200", bar: "#10b981" },
  warning: { label: "Cerca del límite", tone: "bg-amber-100 text-amber-800 border-amber-200", bar: "#f59e0b" },
  over: { label: "Sobre presupuesto", tone: "bg-red-100 text-red-800 border-red-200", bar: "#ef4444" },
};

function currentPeriodMx() {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: "America/Mexico_City",
    year: "numeric",
    month: "2-digit",
  }).format(new Date()).replace("/", "-");
}

function previousPeriod(period) {
  const [y, m] = period.split("-").map(Number);
  if (m === 1) return `${y - 1}-12`;
  return `${y}-${String(m - 1).padStart(2, "0")}`;
}

export default function Presupuesto() {
  const { hasPermission, session } = useAuth();
  const { isSuperAdmin, activeTenantId, branches, activeBranchId } = useTenant();
  const isOwner = isSuperAdmin || session?.user?.role_name === "dueno";
  const canEdit = hasPermission("budgets:edit");

  const [period, setPeriod] = useState(() => currentPeriodMx());
  const [branchId, setBranchId] = useState(() => (isOwner ? "" : activeBranchId || "")); // "" = Consolidado (solo dueño)
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  // Draft budgets keyed by category_id
  const [drafts, setDrafts] = useState({});
  const [dirty, setDirty] = useState(false);

  const tenantParam = isSuperAdmin && activeTenantId ? { tenant_id: activeTenantId } : {};

  const params = useMemo(() => {
    const p = { ...tenantParam, period };
    if (branchId) p.branch_id = branchId;
    return p;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period, branchId, activeTenantId]);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/reports/budget-vs-actual", { params });
      setReport(data);
      const d = {};
      (data.items || []).forEach((r) => {
        d[r.category_id] = r.budget || "";
      });
      setDrafts(d);
      setDirty(false);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  const setDraft = (catId, value) => {
    setDrafts({ ...drafts, [catId]: value });
    setDirty(true);
  };

  const saveAll = async () => {
    setSaving(true);
    try {
      const items = Object.entries(drafts)
        .filter(([, v]) => v !== "" && Number(v) > 0)
        .map(([category_id, amount]) => ({ category_id, amount: Number(amount) }));
      if (!items.length) {
        toast.error("Agrega al menos un presupuesto válido");
        setSaving(false);
        return;
      }
      const body = { period, items };
      if (branchId) body.branch_id = branchId;
      await api.post("/budgets/bulk-upsert", body, { params: tenantParam });
      toast.success("Presupuesto guardado");
      fetchAll();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const copyPrev = async () => {
    try {
      const body = {};
      const q = { ...tenantParam, period };
      if (branchId) q.branch_id = branchId;
      else q.consolidated = true;
      const { data } = await api.post("/budgets/copy-from-previous", body, { params: q });
      if (data.copied === 0) {
        toast.info(`No hay presupuestos en ${previousPeriod(period)} para copiar`);
      } else {
        toast.success(`Copiado ${data.copied} categoría(s) desde ${data.from_period}`);
        fetchAll();
      }
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const items = report?.items || [];
  const totals = report?.totals || {};
  const branchLabel = branchId ? branches.find((b) => b.id === branchId)?.name || "Sucursal" : "Consolidado";

  return (
    <div className="space-y-6" data-testid="page-presupuesto">
      <PageHeader
        title="Presupuesto vs Real"
        description="Planea el gasto mensual por categoría y observa las desviaciones vs los gastos aprobados."
        actions={
          <ExportMenu
            endpoint="/reports/budget-vs-actual"
            params={params}
            permission="budgets:export"
            filename={`presupuesto-${period}`}
            testId="export-budget"
          />
        }
      />

      {/* Filters */}
      <Card className="border-border">
        <CardContent className="p-4 flex flex-col lg:flex-row gap-4 lg:items-end lg:justify-between">
          <div className="flex items-end gap-3">
            <div>
              <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">
                Período
              </Label>
              <div className="relative">
                <Calendar className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  type="month"
                  value={period}
                  onChange={(e) => setPeriod(e.target.value)}
                  className="h-9 w-40 pl-8"
                  data-testid="budget-period"
                />
              </div>
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">
                Alcance
              </Label>
              <Select value={branchId || "__consolidated__"} onValueChange={(v) => setBranchId(v === "__consolidated__" ? "" : v)}>
                <SelectTrigger className="h-9 w-52" data-testid="budget-scope">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {isOwner && <SelectItem value="__consolidated__">Consolidado (tenant)</SelectItem>}
                  {branches.filter((b) => isSuperAdmin || b.id === activeBranchId).map((b) => (
                    <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          {canEdit && (
            <div className="flex items-end gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={copyPrev}
                data-testid="budget-copy-prev"
              >
                <Copy className="h-4 w-4 mr-1" />
                Copiar de {previousPeriod(period)}
              </Button>
              <Button
                size="sm"
                className="btn-tenant"
                disabled={saving || !dirty}
                onClick={saveAll}
                data-testid="budget-save"
              >
                <Save className="h-4 w-4 mr-1" />
                {saving ? "Guardando…" : "Guardar cambios"}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="budget-kpis">
        <KpiCard label="Presupuesto total" value={formatMxn(totals.budget)} icon={Target} loading={loading} testId="kpi-budget" />
        <KpiCard label="Gastado" value={formatMxn(totals.actual)} icon={Wallet} loading={loading} testId="kpi-actual" />
        <KpiCard
          label="Restante"
          value={formatMxn(totals.remaining)}
          hint={`${totals.used_pct_display || 0}% consumido`}
          icon={TrendingUp}
          loading={loading}
          testId="kpi-remaining"
          tone={totals.used_pct >= 1 ? "border-red-200 bg-red-50/30" : ""}
        />
        <KpiCard
          label="Categorías sobre presupuesto"
          value={formatInt((report?.over_categories || []).length)}
          hint={`${formatInt((report?.warning_categories || []).length)} en warning`}
          icon={AlertTriangle}
          loading={loading}
          testId="kpi-over"
        />
      </div>

      {/* Table */}
      <Card className="border-border" data-testid="budget-table">
        <CardHeader className="border-b border-border">
          <CardTitle className="tracking-tight text-base">
            Categorías · {branchLabel} · {period}
          </CardTitle>
          <CardDescription>
            {loading ? "Cargando…" : `${items.length} categoría(s). Warning ≥ 80% · Sobre presupuesto ≥ 100%`}
          </CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-6 space-y-2">
              {[1, 2, 3, 4].map((i) => <Skeleton key={i} className="h-10" />)}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Categoría</TableHead>
                    <TableHead className="text-right w-32">Presupuesto</TableHead>
                    <TableHead className="text-right w-28">Gastado</TableHead>
                    <TableHead className="text-right w-28">Restante</TableHead>
                    <TableHead className="w-64">% Consumido</TableHead>
                    <TableHead className="w-32">Estatus</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {items.map((r) => {
                    const meta = STATUS_META[r.status] || STATUS_META.ok;
                    const pctClamped = Math.min(r.used_pct * 100, 100);
                    return (
                      <TableRow key={r.category_id} data-testid={`budget-row-${r.category_id}`}>
                        <TableCell>
                          <div className="font-medium">{r.category_name}</div>
                          <div className="text-[10px] text-muted-foreground font-mono">{r.category_code}</div>
                        </TableCell>
                        <TableCell className="text-right">
                          {canEdit ? (
                            <Input
                              type="number"
                              step="0.01"
                              min="0"
                              className="h-8 w-28 text-right ml-auto"
                              value={drafts[r.category_id] ?? ""}
                              onChange={(e) => setDraft(r.category_id, e.target.value)}
                              data-testid={`budget-input-${r.category_id}`}
                              placeholder="0.00"
                            />
                          ) : (
                            <span className="font-semibold">{formatMxn(r.budget)}</span>
                          )}
                        </TableCell>
                        <TableCell className="text-right font-medium">{formatMxn(r.actual)}</TableCell>
                        <TableCell className={`text-right ${r.remaining <= 0 && r.budget > 0 ? "text-red-700 font-semibold" : ""}`}>
                          {formatMxn(r.remaining)}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                              <div
                                className="h-full transition-all"
                                style={{
                                  width: `${pctClamped}%`,
                                  backgroundColor: meta.bar,
                                }}
                              />
                            </div>
                            <span className="text-xs font-mono w-14 text-right">
                              {r.used_pct_display}%
                            </span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium border ${meta.tone}`}>
                            {meta.label}
                          </span>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {items.length === 0 && !loading && (
        <div className="text-xs text-muted-foreground text-center">
          Aún no hay categorías con presupuesto o gasto en este período.
          {canEdit && " Comienza asignando montos arriba o copia del mes anterior."}
        </div>
      )}
    </div>
  );
}
